import axios from 'axios';
import debounce from 'lodash/debounce';
import { v4 as uuid } from 'uuid';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';

const CART_DEBOUNCE_TIMER = 500;

const state = {
  reference: '',
  fetched: false,
  syncing: false,
  loadingStatus: false,
  cart: {},
  checkout: {
    stage: -1,
  },
  outOfStockLineItems: [],
};

const getters = {
  isFetched(state) {
    return state.fetched;
  },
  isEmpty(state) {
    return (
      !state.cart ||
      !state.cart.lineItems ||
      !state.cart.lineItems.length ||
      (!getters.getItemLineItemCount(state) && !getters.getSpecialOrderItemLineItemCount(state))
    );
  },
  isSyncing(state) {
    return state.syncing;
  },
  getSyncReference(state) {
    return state.reference;
  },
  getItemLineItemCount(state) {
    const lineItems = getters
      .getLineItemsOfType(state)(1)
      .filter(lineItem => lineItem.quantity > 0);

    return lineItems.length;
  },
  getTotals(state) {
    if (!state.cart) {
      return {};
    }

    return {
      sub: state.cart.rawSubTotal,
      discountSub: state.cart.subTotal,
      tax: state.cart.taxTotal,
      due: state.cart.dueTotal,
      grand: state.cart.grandTotal,
      shipping: state.cart.shippingTotal,
    };
  },
  getLineItems(state) {
    return state.cart && state.cart.lineItems ? state.cart.lineItems : [];
  },
  getLineItemsOfType(state) {
    return type => {
      return getters.getLineItems(state).filter(lineItem => lineItem.type === type);
    };
  },

  getItemLineItem(state) {
    return (item, variation = null) => {
      if (!item) {
        return null;
      }

      return getters
        .getLineItemsOfType(state)(1)
        .find(lineItem => {
          return (
            lineItem.item.id === item.id &&
            ((!lineItem.variation && !variation) || lineItem.variation === variation)
          );
        });
    };
  },
  getCartSyncDto(state) {
    const { cart, checkout } = state;

    const items = cart.lineItems
      ? cart.lineItems
          .filter(lineItem => lineItem.item)
          .map(lineItem => {
            return {
              embeddedPrice: lineItem.embeddedPrice,
              originalPrice: lineItem.originalPrice,
              embeddedDiscount: lineItem.embeddedDiscount,
              expiration: lineItem.expiration,
              id: lineItem.id,
              item: lineItem.item.id,
              variation: lineItem.variation,
              quantity: lineItem.quantity,
              substitutable: lineItem.substitutable,
              instructions: lineItem.instructions,
              weightedItemQuantity: lineItem.weightedItemQuantity,
            };
          })
      : [];

    const giftCardItems = cart.giftCardItems
      ? cart.giftCardItems.map(lineItem => ({ ...lineItem, design: lineItem.design.id }))
      : [];

    const specialOrderItems = cart.specialOrderItems;

    return {
      transaction: cart.id,
      instructions: cart.instructions,
      items,
      coupons: cart.coupons,
      giftCardItems,
      deferrableCombos: cart.deferrableCombos,
      inStore: cart.inStore,
      specialOrderItems,
      checkout,
    };
  },
  getBilling(state) {
    return state.checkout.billing;
  },
  getShipping(state) {
    return state.checkout.shipping;
  },
  getCheckoutStage(state) {
    return state.checkout.stage;
  },
  getMaxStage(state) {
    return state.checkout.maxStage;
  },
  getMaxStageReached(state) {
    return state.checkout.maxStageReached;
  },
  getShippingMethods(state) {
    return state.checkout.shippingMethods;
  },
  getShippingMethod(state) {
    return state.checkout.shippingMethod;
  },
  getReservationDate(state) {
    return state.checkout.reservationDate;
  },
  getReservationTime(state) {
    return state.checkout.reservationTime;
  },
  getPaymentMethods(state) {
    return state.checkout.paymentMethods;
  },
  getOrderInstructions(state) {
    return state.cart.instructions;
  },
  getInStore(state) {
    return state.cart.inStore;
  },
  getCoupons(state) {
    return state.cart.coupons;
  },
  getPayLater(state) {
    return state.cart.payLater;
  },
  getAddedCouponValid(state) {
    return state.cart.addedCouponValid;
  },
  getDeferrableCombo(state) {
    return id => {
      return getters.getDeferrableCombos(state).find(promo => {
        return promo.id === id;
      });
    };
  },
  getDeferrableCombos(state) {
    return state.cart.deferrableCombos;
  },
  getGiftCardItems(state) {
    return state.cart && state.cart.giftCardItems ? state.cart.giftCardItems : [];
  },
  getGiftCardTotals(state) {
    return getters.getGiftCardItems(state).reduce((a, b) => a + b.amount, 0);
  },
  getSpecialOrderItems(state) {
    return state.cart && state.cart.specialOrderItems ? state.cart.specialOrderItems : [];
  },
  getSpecialOrderItemLineItemCount(state) {
    const lineItems = getters
      .getLineItemsOfType(state)(60)
      .filter(lineItem => lineItem.quantity > 0);

    return lineItems.length;
  },
  getCartLineItemCount(state) {
    return state.cart &&
      (state.cart.giftCardItems || state.cart.specialOrderItems || state.cart.lineItems)
      ? getters.getItemLineItemCount(state) +
          getters.getGiftCardItems(state).length +
          getters.getSpecialOrderItemLineItemCount(state)
      : 0;
  },
  isGiftCard(state) {
    return getters.getGiftCardItems(state).length > 0;
  },
  getOutOfStockLineItems(state) {
    return state.outOfStockLineItems;
  },
  getOutOfStockLineItemsOfType(state) {
    return type => {
      return getters.getOutOfStockLineItems(state)?.filter(lineItem => lineItem.type === type);
    };
  },
  getOutOfStockItemLineItem(state) {
    return (item, variation = null) => {
      if (!item) {
        return null;
      }

      return getters
        .getOutOfStockLineItemsOfType(state)(1)
        .find(lineItem => {
          return (
            lineItem.item.id === item.id &&
            ((!lineItem.variation && !variation) || lineItem.variation === variation)
          );
        });
    };
  },
  isLoading(state) {
    return state.loadingStatus;
  },
  // fetch web email from the checkout.emails
  getCheckoutWebEmail(state) {
    return (
      (!!state.checkout.emails &&
        state.checkout.emails.find(emailObj => emailObj.name === 'Web')?.email) ||
      ''
    );
  },
  // fetch web phone number from the checkout.phones
  getCheckoutWebPhone(state) {
    return (
      (!!state.checkout.phones &&
        state.checkout.phones.find(phoneObj => phoneObj.name === 'Web')?.phone) ||
      ''
    );
  },
  isProhibitedItemsInCart(state) {
    const lineItems = getters.getLineItemsOfType(state)(1);

    let prohibitedItems = false;

    lineItems.forEach(item => {
      if (
        item.item.hasTimeRestriction ||
        (item.item.weightProfile && !item.embeddedPrice) ||
        item.item.hasAgeRestriction ||
        item.item.cashierApprovalRequired ||
        item.item.hasRestriction
      ) {
        prohibitedItems = true;
      }
    });
    return prohibitedItems;
  },
};

export const SET_SYNCING = 'SET_SYNCING';
export const SET_FETCHED = 'SET_FETCHED';
export const SET_SYNC_REFERENCE = 'SET_SYNC_REFERENCE';
export const SET_CART = 'SET_CART';
export const SET_CHECKOUT = 'SET_CHECKOUT';
export const SET_ITEM_LINEITEM_QUANTITY = 'SET_ITEM_LINEITEM_QUANTITY';
export const SET_ALL_CART_ITEMS_QUANTITY = 'SET_ALL_CART_ITEMS_QUANTITY';
export const SET_OUTOFSTOCK_LINEITEM_QUANTITY = 'SET_OUTOFSTOCK_LINEITEM_QUANTITY';
export const SET_OUTOFSTOCK_LINEITEMS = 'SET_OUTOFSTOCK_LINEITEMS';
export const ADD_GIFT_CARD = 'ADD_GIFT_CARD';
export const REMOVE_GIFT_CARD = 'REMOVE_GIFT_CARD';
export const REMOVE_ALL_GIFT_CARDS = 'REMOVE_ALL_GIFT_CARDS';
export const SET_ITEM_LINEITEM_INSTRUCTIONS = 'SET_ITEM_LINEITEM_INSTRUCTIONS';
export const SET_ORDER_INSTRUCTIONS = 'SET_ORDER_INSTRUCTIONS';
export const SET_ITEM_LINEITEM_SUBSTITUTABLE = 'SET_ITEM_LINEITEM_SUBSTITUTABLE';
export const SET_BILLING = 'SET_BILLING';
export const SET_SHIPPING = 'SET_SHIPPING';
export const SET_CHECKOUT_STAGE = 'SET_CHECKOUT_STAGE';
export const SET_MAX_STAGE = 'SET_MAX_STAGE';
export const SET_MAX_STAGE_REACHED = 'SET_MAX_STAGE_REACHED';
export const SET_SHIPPING_METHOD = 'SET_SHIPPING_METHOD';
export const SET_SHIPPING_METHODS = 'SET_SHIPPING_METHODS';
export const SET_RESERVATION_DATE = 'SET_RESERVATION_DATE';
export const SET_RESERVATION_TIME = 'SET_RESERVATION_TIME';
export const SET_DEFERRABLE_COMBOS = 'SET_DEFERRABLE_COMBOS';
export const SET_MTO_ITEM = 'SET_MTO_ITEM';
export const SET_ITEM_INSTRUCTIONS = 'SET_ITEM_INSTRUCTIONS';
export const SET_LOADING_STATUS = 'SET_LOADING_STATUS';
export const SET_IN_STORE = 'SET_IN_STORE';
export const ADD_SPECIAL_ORDER_ITEM = 'ADD_SPECIAL_ORDER_ITEM';
export const UPDATE_SPECIAL_ORDER_ITEM = 'UPDATE_SPECIAL_ORDER_ITEM';
export const REMOVE_SPECIAL_ORDER_ITEM = 'REMOVE_SPECIAL_ORDER_ITEM';
const mutations = {
  [SET_SYNCING](state, syncing) {
    state.syncing = syncing;
  },
  [SET_FETCHED](state, fetched) {
    state.fetched = fetched;
  },
  [SET_SYNC_REFERENCE](state, reference) {
    state.reference = reference;
  },
  [SET_CART](state, cart) {
    state.cart = cart;
  },
  [SET_CHECKOUT](state, checkout) {
    state.checkout = checkout;

    if (state.checkout.billing) {
      // Set billing.email value from checkout.email when billing.email is null/undefined/empty
      state.checkout.billing.email =
        state.checkout.billing.email || getters.getCheckoutWebEmail(state);

      // Set billing.phone value from checkout.phones when billing.phone is null/undefined/empty
      state.checkout.billing.phone =
        state.checkout.billing.phone || getters.getCheckoutWebPhone(state);
    }
  },
  [SET_ITEM_LINEITEM_QUANTITY](state, payload) {
    const { item, variation, weightedItemQuantity, quantity = 1, priceEmbeddedLineItem } = payload;
    let itemLineItem = getters.getItemLineItem(state)(item, variation);
    let newItemLineItem = false;

    if (!itemLineItem && !priceEmbeddedLineItem) {
      newItemLineItem = true;
      itemLineItem = {
        type: 1,
        item,
        variation,
        quantity,
        weightedItemQuantity,
      };
    } else if (!itemLineItem && priceEmbeddedLineItem) {
      newItemLineItem = true;
      itemLineItem = {
        type: 1,
        item,
        variation,
        quantity,
        weightedItemQuantity,
        ...priceEmbeddedLineItem,
      };
    }

    itemLineItem.quantity = quantity;
    itemLineItem.weightedItemQuantity = weightedItemQuantity;

    if (newItemLineItem) {
      state.cart.lineItems = [
        ...state.cart.lineItems.filter(lineItem => lineItem !== itemLineItem),
        {
          ...itemLineItem,
          quantity,
        },
      ];
    }
  },

  [SET_ALL_CART_ITEMS_QUANTITY](state, payload) {
    const { quantity } = payload;
    state.cart.lineItems.forEach(lineItem => {
      // eslint-disable-next-line no-param-reassign
      lineItem.quantity = quantity;
    });
  },

  [SET_MTO_ITEM](state, payload) {
    const { lineItem, item, variation, amount, instructions = '' } = payload;
    let line = getters.getItemLineItem(state)(item, lineItem?.variation);
    // If there is an identical lineItem to one being added, remove it and update the cart
    if (line && lineItem) {
      line = {
        type: 1,
        item: lineItem.item,
        variation: lineItem.variation,
        quantity: 0,
        amount: lineItem.amount,
        instructions,
      };
      state.cart.lineItems = [
        ...state.cart.lineItems.filter(l => l !== line),
        {
          ...line,
        },
      ];
    }

    // Update the line item and add it to the cart
    line = {
      type: 1,
      item,
      variation,
      quantity: lineItem && lineItem.quantity ? lineItem.quantity : 1,
      amount,
      instructions,
    };
    state.cart.lineItems = [
      ...state.cart.lineItems.filter(l => l !== line),
      {
        ...line,
      },
    ];

    AnalyticsService.track(TrackableEventConstants.ItemAddedToCart, { items: [item] });
  },

  [SET_ITEM_LINEITEM_INSTRUCTIONS](state, payload) {
    const { item, variation, instructions = '' } = payload;
    const itemLineItem = getters.getItemLineItem(state)(item, variation);

    if (!itemLineItem) {
      return;
    }

    itemLineItem.instructions = instructions;
  },

  [SET_ORDER_INSTRUCTIONS](state, instructions) {
    state.cart.instructions = instructions;
  },
  [SET_ITEM_LINEITEM_SUBSTITUTABLE](state, payload) {
    const { item, variation, substitutable = false } = payload;
    const itemLineItem = getters.getItemLineItem(state)(item, variation);

    if (!itemLineItem) {
      return;
    }

    itemLineItem.substitutable = substitutable;
  },

  [SET_BILLING](state, payload) {
    const { firstName, lastName, email, phone } = payload;
    const { street1, street2, city, postal, country } = state.checkout.shipping;
    // For now, the billing and shipping info will be combined. This will change
    state.checkout.billing = {
      firstName,
      lastName,
      email,
      phone,
      street1,
      street2,
      city,
      postal,
      state: state.checkout.shipping.state,
      country,
    };
  },
  [SET_SHIPPING](state, payload) {
    const { firstName, lastName, email, phone } = state.checkout.billing;
    const { street1, street2, city, postal, country, latitude, longitude } = payload;
    // For now, the billing and shipping info will be combined. This will change
    state.checkout.shipping = {
      firstName,
      lastName,
      email,
      phone,
      street1,
      street2,
      city,
      postal,
      state: payload.state,
      country,
      latitude,
      longitude,
    };
  },
  [SET_CHECKOUT_STAGE](state, stage) {
    state.checkout.stage = stage;
  },
  [SET_IN_STORE](state, payload) {
    state.cart.inStore = payload;
  },
  [SET_MAX_STAGE](state, stage) {
    state.checkout.maxStage = stage;
  },
  [SET_MAX_STAGE_REACHED](state, stage) {
    state.checkout.maxStageReached = stage;
  },
  [SET_SHIPPING_METHOD](state, method) {
    state.checkout.shippingMethod = method;
  },
  [SET_SHIPPING_METHODS](state, methods) {
    state.checkout.shippingMethods = [...methods];
  },
  [SET_RESERVATION_DATE](state, date) {
    state.checkout.reservationDate = date;
  },
  [SET_RESERVATION_TIME](state, time) {
    state.checkout.reservationTime = time;
  },
  [SET_DEFERRABLE_COMBOS](state, payload) {
    const { id, defer } = payload;
    const promo = getters.getDeferrableCombo(state)(id);

    if (!promo) {
      return;
    }

    promo.deferred = defer;
  },
  [ADD_GIFT_CARD](state, giftCard) {
    state.cart.giftCardItems = [
      ...state.cart.giftCardItems.filter(gc => gc.id !== giftCard.id),
      { ...giftCard, quantity: 1 },
    ];

    AnalyticsService.track(TrackableEventConstants.ItemAddedToCart, { items: [giftCard] });
  },
  [REMOVE_GIFT_CARD](state, giftCard) {
    state.cart.giftCardItems = [
      ...state.cart.giftCardItems.filter(gc => gc.id !== giftCard.id),
      { ...giftCard, quantity: 0 },
    ];

    AnalyticsService.track(TrackableEventConstants.ItemRemovedFromCart, { items: [giftCard] });
  },
  [REMOVE_ALL_GIFT_CARDS](state) {
    state.cart.giftCardItems.forEach(lineItem => {
      // eslint-disable-next-line no-param-reassign
      lineItem.quantity = 0;
    });

    AnalyticsService.track(TrackableEventConstants.ItemRemovedFromCart, {
      items: state.cart.giftCardItems,
    });
  },
  [ADD_SPECIAL_ORDER_ITEM](state, payload) {
    const { description } = payload;

    // Update the line item and add it to the cart
    const line = {
      type: 60,
      description,
      quantity: 1,
    };
    state.cart.lineItems = [
      ...state.cart.lineItems.filter(so => so !== line),
      {
        ...line,
      },
    ];
    state.cart.specialOrderItems = [
      ...state.cart.lineItems.filter(so => so === line && so.instructions),
      {
        ...line,
      },
    ];
  },
  [UPDATE_SPECIAL_ORDER_ITEM](state, payload) {
    const lineItem = payload;

    const specialOrderItems = state.cart.lineItems.filter(li => li.type === 60);
    state.cart.specialOrderItems = specialOrderItems
      .filter(specialOrderItem => specialOrderItem.instructions === lineItem.instructions)
      .map(specialOrderItem => {
        return {
          id: specialOrderItem.id,
          description: specialOrderItem.instructions,
          quantity: 1,
        };
      });
    state.cart.lineItems = [
      ...state.cart.lineItems.filter(so => so !== lineItem),
      {
        ...lineItem,
      },
    ];
  },
  [REMOVE_SPECIAL_ORDER_ITEM](state, payload) {
    const lineItem = payload;

    const specialOrderItems = state.cart.lineItems.filter(li => li.type === 60);
    if (specialOrderItems) {
      state.cart.specialOrderItems = specialOrderItems
        .filter(specialOrderItem => specialOrderItem.instructions === lineItem.instructions)
        .map(specialOrderItem => {
          return {
            id: specialOrderItem.id,
            description: specialOrderItem.instructions,
            quantity: 0,
          };
        });
      state.cart.lineItems = [
        ...state.cart.lineItems.filter(so => so !== lineItem),
        {
          ...lineItem,
          quantity: 0,
        },
      ];
    }
  },
  [SET_OUTOFSTOCK_LINEITEM_QUANTITY](state, payload) {
    const { line = {}, quantity = 1 } = payload;
    const itemLineItem = getters.getOutOfStockItemLineItem(state)(line.item, line.variation);

    if (quantity === 0) {
      state.outOfStockLineItems = [
        ...state.outOfStockLineItems.filter(lineItem => lineItem !== itemLineItem),
      ];
    } else if (state.outOfStockLineItems && state.outOfStockLineItems.length > 0) {
      const outOfStockItemIdx = state.outOfStockLineItems.findIndex(
        outOfStockItem => outOfStockItem.item.id === line.item.id,
      );
      // To remove duplicate items in the out of stock items list
      if (outOfStockItemIdx === -1) {
        state.outOfStockLineItems.push(line);
      }
    } else {
      state.outOfStockLineItems = [line];
    }
  },
  [SET_OUTOFSTOCK_LINEITEMS](state, payload) {
    const { lines = [] } = payload;
    state.outOfStockLineItems = [...lines];
  },
  [SET_LOADING_STATUS](state, status) {
    state.loadingStatus = status;
  },
};

const actions = {
  async reload({ commit, dispatch }) {
    commit(SET_SYNCING, true);
    try {
      const response = await axios.get('api/cart');
      const { checkout, ...cart } = response.data;

      commit(SET_CHECKOUT, checkout);
      commit(SET_CART, cart);

      dispatch('checkForOutOfStockLineItems', cart.lineItems);
    } catch (error) {
      console.error(error);
    } finally {
      commit(SET_SYNCING, false);
      commit(SET_FETCHED, true);
    }
  },

  // Debounced function to delay cart sync so that it only fires a max of once every 0.5s
  defer: debounce(({ dispatch }) => dispatch('sync', { immediate: true }), CART_DEBOUNCE_TIMER),

  // Primary method of syncing the cart with optional parameter to force immediate sync
  async sync({ commit, getters, dispatch }, payload = { immediate: false }) {
    if (!getters.isSyncing) {
      commit(SET_SYNCING, true);
    }

    // Store a reference in the state so that we can check if the cart has been modified before applying updates from the server
    const reference = uuid();
    commit(SET_SYNC_REFERENCE, reference);

    if (!payload.immediate) {
      dispatch('defer');
    } else {
      try {
        // The GET request is only wait to get out of stock cart items from backend since the POST request removes them in the backend
        const getResponse = await axios.get('api/cart');
        const { ...getCart } = getResponse.data;

        // If the inStore flag is set in Scan and Go on the backend, make sure it is set again on the frontend before syncing the cart
        if (getCart.inStore) {
          commit(SET_IN_STORE, getCart.inStore);
        }
        const cartDto = await getters.getCartSyncDto;
        const response = await axios.post('api/cart', cartDto);

        // Determine if changes have occurred during network transmission
        if (getters.getSyncReference === reference) {
          const { checkout, ...cart } = response.data;
          commit(SET_CHECKOUT, checkout);
          commit(SET_CART, cart);
          commit(SET_SYNCING, false);

          if (!this.state.user.user.scango) {
            dispatch('checkForOutOfStockLineItems', getCart.lineItems);
          }
        }
      } catch (error) {
        console.error(error);
        commit(SET_SYNCING, false);
      }
    }
  },
  checkForOutOfStockLineItems({ commit, dispatch }, lines) {
    if (this.state.config.sellOutOfStock || this.state.user.user.scango) {
      return;
    }

    if (lines && lines.length === 0) {
      dispatch('clearOutOfStockItems');
    }

    lines.forEach(line => {
      if (line.item && line.item.outOfStock) {
        commit(SET_OUTOFSTOCK_LINEITEM_QUANTITY, {
          line,
          quantity: 1,
        });

        dispatch('setItemQuantity', {
          item: line.item,
          variation: line.variation,
          quantity: 0,
        });
      }
    });
  },
  async setItemQuantity({ commit, dispatch, getters }, payload) {
    // If user changes items in their cart while not on the CO page, make them reconfirm on the 1st stage
    await dispatch('checkout', { newStage: 0, modified: false });
    if (!getters.getGiftCardItems.length) {
      await commit(SET_ITEM_LINEITEM_QUANTITY, payload);

      await dispatch('sync', { immediate: true });
    }
  },
  async removeAllCartItems({ commit, dispatch, getters }) {
    if (!getters.getGiftCardItems.length) {
      const specialOrderItems = state.cart.lineItems.filter(li => li.type === 60);
      state.cart.specialOrderItems = specialOrderItems.map(specialOrderItem => {
        return {
          id: specialOrderItem.id,
          description: specialOrderItem.instructions,
          quantity: 0,
        };
      });
      await commit(SET_ALL_CART_ITEMS_QUANTITY, { quantity: 0 });
      AnalyticsService.track(TrackableEventConstants.ItemRemovedFromCart, {
        items: state.cart.lineItems
          ?.filter(lineItem => lineItem.item)
          .map(lineItem => lineItem.item),
      });

      await dispatch('sync', { immediate: true });
    } else {
      commit(REMOVE_ALL_GIFT_CARDS);
      dispatch('sync');
    }
  },
  clearOutOfStockItems({ commit }) {
    commit(SET_OUTOFSTOCK_LINEITEMS, []);
  },
  setMtoItem({ commit, dispatch }, payload) {
    commit(SET_MTO_ITEM, payload);
    dispatch('sync');
  },
  async addGiftCard({ commit, dispatch }, giftCardItems) {
    await dispatch('checkout', { newStage: 0, modified: false });
    await commit(ADD_GIFT_CARD, giftCardItems);
    await dispatch('sync', { immediate: true });
  },
  async removeGiftCard({ commit, dispatch }, giftCardItems) {
    await commit(REMOVE_GIFT_CARD, giftCardItems);

    await dispatch('sync', { immediate: true });
  },

  setItemLineItemInstructions({ commit }, payload) {
    commit(SET_ITEM_LINEITEM_INSTRUCTIONS, payload);
  },
  setInStore({ commit }, payload) {
    commit(SET_IN_STORE, payload);
  },
  setItemLineItemSubstitutable({ commit }, payload) {
    commit(SET_ITEM_LINEITEM_SUBSTITUTABLE, payload);
  },
  setReservation({ commit }, reservation) {
    commit(SET_RESERVATION_DATE, reservation.date);
    commit(SET_RESERVATION_TIME, reservation.time);
  },
  addCoupon({ state, dispatch }, coupon) {
    if (!state.cart.coupons) {
      state.cart.coupons = [];
    }
    const existingCoupon = state.cart.coupons.find(c => c.code === coupon);

    if (!existingCoupon) {
      state.cart.coupons.push({
        code: coupon,
        remove: false,
      });

      dispatch('sync');
    }
  },
  removeCoupon({ state, dispatch }, coupon) {
    if (state.cart.coupons) {
      let removed = false;

      state.cart.coupons.forEach(c => {
        if (c.code === coupon) {
          // eslint-disable-next-line no-param-reassign
          c.remove = true;
          removed = true;
        }
      });

      if (removed) {
        dispatch('sync');
      }
    }
  },

  setDeferrableCombos({ commit }, payload) {
    commit(SET_DEFERRABLE_COMBOS, payload);
    // dispatch('sync');
  },
  clearCart({ commit }) {
    commit(SET_CART, {});
  },
  clearCheckout({ commit }) {
    commit(SET_CHECKOUT, {});
  },
  clearCartAndCheckout({ commit }) {
    commit(SET_CART, {});
    commit(SET_CHECKOUT, {});
  },
  /**
   * Advances the current stage either directly or by contacting the server first to validate stage data
   * @modified - if stage being left was modified. If so, need to set maxStage too
   */
  checkout({ commit, dispatch }, { newStage, modified = false }) {
    const { checkout } = state;

    // newStage <= checkout.maxStage: Prevents issues related to going to stage and getting kicked back
    if (newStage <= checkout.maxStage) {
      if (newStage > checkout.maxStageReached) {
        commit(SET_MAX_STAGE_REACHED, newStage);
      } else if (checkout.stage > checkout.maxStageReached) {
        commit(SET_MAX_STAGE_REACHED, checkout.stage);
      }
    }

    if (newStage) {
      AnalyticsService.track(TrackableEventConstants.CheckoutStageChanged, {
        stage: newStage,
        lineItems: state.cart.lineItems,
      });
    }
    // If we know the requested checkout stage is valid go directly to it
    if (checkout && newStage <= checkout.maxStage && !modified) {
      commit(SET_CHECKOUT_STAGE, newStage);
      return;
    }

    // If we don't know if the stage is valid then contact the server to validate the current state of the cart
    commit(SET_CHECKOUT_STAGE, newStage);
    dispatch('sync');
  },
  setLoadingStatus({ commit }, status) {
    commit(SET_LOADING_STATUS, status);
  },
  addSpecialOrderItem({ commit, dispatch }, payload) {
    if (!state.cart.specialOrderItems) {
      state.cart.specialOrderItems = [];
    }
    commit(ADD_SPECIAL_ORDER_ITEM, payload);
    dispatch('sync');
  },
  updateSpecialOrderItem({ commit, dispatch }, payload) {
    commit(UPDATE_SPECIAL_ORDER_ITEM, payload);
    dispatch('sync');
  },
  removeSpecialOrderItem({ commit, dispatch }, payload) {
    commit(REMOVE_SPECIAL_ORDER_ITEM, payload);
    dispatch('sync');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
